import React, { useState, useEffect, useMemo } from 'react';
import { Card } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import DataApi from '../../api/DataApi';
import ScoringRuleApi from '../../api/ScoringRuleApi';
import { theme } from '../../theme/theme.js';
import './ScoreDisplay.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

/**
 * ScoreDisplay Component
 * Displays lead score or any object score with historical trend visualization
 * Shows positive values, negative values, total score, and percentages
 * Includes historical trend graph with threshold lines and event markers
 *
 * @param {string} objectName - The object name (e.g., 'lead', 'account')
 * @param {string} recordId - The record ID to get score for
 */
const ScoreDisplay = ({ objectName, recordId, embedded = false }) => {
  const [scoreData, setScoreData] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [appliedRules, setAppliedRules] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [maxPossibleScore, setMaxPossibleScore] = useState(100); // Default to 100, will be calculated from rules
  const [recordFields, setRecordFields] = useState([]); // Store record fields to access actual values
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasScoringRule, setHasScoringRule] = useState(false);
  // Removed history view - only showing current view

  useEffect(() => {
    if (!objectName || !recordId) {
      setLoading(false);
      return;
    }

    fetchScoreData();
  }, [objectName, recordId]);

  // Removed history fetching - not needed for current view only

  // Helper function to calculate maximum possible score from scoring rules
  const calculateMaxPossibleScore = (config) => {
    console.log('calculateMaxPossibleScore - Full config:', JSON.stringify(config, null, 2));

    if (!config || !config.logic || !config.logic.conditions) {
      console.log('calculateMaxPossibleScore - No conditions found, returning 100');
      return 100; // Default fallback
    }

    console.log('calculateMaxPossibleScore - Conditions:', config.logic.conditions);

    // Group conditions by field and find max score per field
    const fieldGroups = new Map();

    config.logic.conditions.forEach((condition, index) => {
      const fieldName = condition.field || 'unknown';
      const score = parseInt(condition.score) || 0;
      const operation = condition.scoreOperation || 'add';

      console.log(`Condition ${index}: field=${fieldName}, score=${score}, operation=${operation}`);

      // Only consider positive scores (add operations) for maximum calculation
      // Ignore subtract operations as they reduce the score
      if (operation === 'subtract') {
        console.log(`  Skipping subtract condition for ${fieldName}`);
        return; // Skip negative conditions
      }

      const actualScore = score; // Only positive scores

      if (!fieldGroups.has(fieldName)) {
        fieldGroups.set(fieldName, {
          maxPositive: 0
        });
        console.log(`  Created field group for ${fieldName}`);
      }

      const group = fieldGroups.get(fieldName);

      if (actualScore > 0 && actualScore > group.maxPositive) {
        console.log(`  Updating max for ${fieldName}: ${group.maxPositive} -> ${actualScore}`);
        group.maxPositive = actualScore;
      } else {
        console.log(`  Not updating max for ${fieldName}: current=${group.maxPositive}, new=${actualScore}`);
      }
    });

    // Sum max positive scores across all fields (best case scenario)
    let maxPositiveTotal = 0;

    for (const [fieldName, group] of fieldGroups.entries()) {
      console.log(`Field ${fieldName}: maxPositive = ${group.maxPositive}`);
      maxPositiveTotal += group.maxPositive;
    }

    console.log('calculateMaxPossibleScore - Total maxPositiveTotal:', maxPositiveTotal);

    // Maximum possible score = sum of max positive scores from each field
    // Return the calculated value, or 100 as fallback if calculation is 0 or invalid
    const result = maxPositiveTotal > 0 ? maxPositiveTotal : 100;
    console.log('calculateMaxPossibleScore - Final result:', result);
    return result;
  };

  const fetchScoreData = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to get score from record data
      const dataApi = new DataApi(`object/view/${objectName}`);
      const response = await dataApi.fetchById(recordId);
      const recordData = response?.data?.data;

      if (!recordData) {
        throw new Error('Record not found');
      }

      // Extract score fields from record data
      const allFields = [
        ...(recordData.highlightSection || []),
        ...(recordData.details || []).flatMap(section => section.fields || []),
        ...(recordData.otherHiddenFields || [])
      ];

      // Look for common score field names
      const getFieldValue = (fieldName) => {
        const field = allFields.find(
          f => f?.fieldName?.toLowerCase() === fieldName.toLowerCase()
        );
        return field?.value !== null && field?.value !== undefined
          ? parseFloat(field.value) || 0
          : null;
      };

      // Try to get field names from scoring rule configuration
      let scoreFieldNames = {
        totalScore: ['totalscore', 'total_score', 'lead_score', 'score'],
        totalPositive: ['totalpositive', 'total_positive', 'positive_score', 'total_positive_score'],
        totalNegative: ['totalnegative', 'total_negative', 'negative_score', 'total_negative_score']
      };

      // Try to fetch scoring rule to get configured field names and calculate max possible score
      let activeScoringRuleFound = false;
      try {
        const scoringRuleApi = new ScoringRuleApi();
        const rulesResponse = await scoringRuleApi.fetchByObject(objectName, true);
        if (rulesResponse?.data?.success && rulesResponse.data.data?.length > 0) {
          const rule = rulesResponse.data.data[0];

          // Check if rule is active
          if (rule.is_active === true || rule.is_active === undefined) {
            activeScoringRuleFound = true;
            setHasScoringRule(true);
            const config = typeof rule.config === 'string' ? JSON.parse(rule.config) : rule.config;

            if (config.scoreStorage) {
              // Use configured field names, but keep fallbacks
              if (config.scoreStorage.totalScoreField) {
                scoreFieldNames.totalScore.unshift(config.scoreStorage.totalScoreField);
              }
              if (config.scoreStorage.totalPositiveField) {
                scoreFieldNames.totalPositive.unshift(config.scoreStorage.totalPositiveField);
              }
              if (config.scoreStorage.totalNegativeField) {
                scoreFieldNames.totalNegative.unshift(config.scoreStorage.totalNegativeField);
              }
            }

            // Calculate maximum possible score from rules
            const calculatedMaxScore = calculateMaxPossibleScore(config);
            console.log('Calculated max possible score:', calculatedMaxScore);
            setMaxPossibleScore(calculatedMaxScore);
          } else {
            setHasScoringRule(false);
          }
        } else {
          setHasScoringRule(false);
        }
      } catch (err) {
        console.warn('Could not fetch scoring rule config:', err);
        setHasScoringRule(false);
      }

      // If no active scoring rule found, set hasScoringRule to false
      // DetailLayout will handle visibility, so we don't return early here
      if (!activeScoringRuleFound) {
        setHasScoringRule(false);
        setLoading(false);
        // Don't return early - let DetailLayout control visibility
        return;
      }

      // Try each field name in priority order
      let totalPositive = null;
      let totalNegative = null;
      let totalScore = null;

      for (const fieldName of scoreFieldNames.totalPositive) {
        const value = getFieldValue(fieldName);
        if (value !== null) {
          totalPositive = value;
          break;
        }
      }

      for (const fieldName of scoreFieldNames.totalNegative) {
        const value = getFieldValue(fieldName);
        if (value !== null) {
          totalNegative = value;
          break;
        }
      }

      for (const fieldName of scoreFieldNames.totalScore) {
        const value = getFieldValue(fieldName);
        if (value !== null) {
          totalScore = value;
          break;
        }
      }

      // Always try to evaluate to get applied rules (for showing positive/negative points)
      let evaluationAppliedRules = [];
      try {
        // Flatten record data for evaluation
        const flatRecordData = {};
        allFields.forEach(field => {
          if (field.fieldName && field.value !== null && field.value !== undefined) {
            flatRecordData[field.fieldName] = field.value;
          }
        });

        // Evaluate score using scoring rules API to get applied rules
        const scoringRuleApi = new ScoringRuleApi();
        const evaluationResponse = await scoringRuleApi.evaluate(objectName, flatRecordData);

        if (evaluationResponse?.data?.success) {
          const evaluation = evaluationResponse.data.data;

          // If we don't have score values, use evaluation results
          if (totalPositive === null && totalNegative === null && totalScore === null) {
            totalPositive = evaluation.totalPositive || 0;
            totalNegative = evaluation.totalNegative || 0;
            totalScore = evaluation.totalScore || 0;
          }

          // Always store applied rules for display
          evaluationAppliedRules = evaluation.appliedRules || [];
        }
      } catch (evalError) {
        console.warn('Could not evaluate score:', evalError);
        // If evaluation fails (e.g., due to permissions), try to use defaults
        if (totalPositive === null) totalPositive = 0;
        if (totalNegative === null) totalNegative = 0;
        if (totalScore === null) totalScore = 0;
      }

      if (totalPositive !== null || totalNegative !== null || totalScore !== null) {
        // If we have some but not all values, calculate missing ones
        if (totalScore === null && totalPositive !== null && totalNegative !== null) {
          totalScore = totalPositive - totalNegative;
        } else if (totalPositive === null && totalScore !== null && totalNegative !== null) {
          totalPositive = totalScore + totalNegative;
        } else if (totalNegative === null && totalScore !== null && totalPositive !== null) {
          totalNegative = totalPositive - totalScore;
        }
      }

      // Ensure we have valid numbers
      totalPositive = totalPositive || 0;
      totalNegative = totalNegative || 0;
      totalScore = totalScore !== null ? totalScore : (totalPositive - totalNegative);

      // Store record fields for accessing actual values
      setRecordFields(allFields);
      setAppliedRules(evaluationAppliedRules);

      setScoreData({
        totalPositive,
        totalNegative,
        totalScore
      });

      // Store applied rules for displaying positive/negative points
      setAppliedRules(evaluationAppliedRules);
    } catch (err) {
      console.error('Error fetching score data:', err);
      setError(err.message || 'Failed to load score data');
    } finally {
      setLoading(false);
    }
  };

  const fetchScoreHistory = async () => {
    try {
      const scoringRuleApi = new ScoringRuleApi();
      const days = 90; // Default to 90 days since history view is removed
      const response = await scoringRuleApi.getHistory(objectName, recordId, days);

      if (response?.data?.success) {
        setScoreHistory(response.data.data || []);
      }
    } catch (err) {
      console.warn('Could not fetch score history:', err);
      // Don't set error - history is optional
      setScoreHistory([]);
    }
  };

  // Calculate supporting metrics
  const metrics = useMemo(() => {
    if (!scoreData || scoreHistory.length === 0) {
      return {
        currentScore: scoreData?.totalScore || 0,
        sevenDayChange: 0,
        allTimeHigh: scoreData?.totalScore || 0,
        allTimeHighDate: null,
        timeAsMQL: 0,
        timeAsSQL: 0
      };
    }

    const currentScore = scoreData.totalScore;
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Find score 7 days ago
    const sevenDaysAgoScore = scoreHistory
      .filter(h => new Date(h.timestamp) <= sevenDaysAgo)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]?.totalScore || currentScore;

    // Find all-time high from history only (not current score)
    // Get all totalScore values from history
    const historyScores = scoreHistory.map(h => h.totalScore || 0);
    const maxHistoryScore = historyScores.length > 0 ? Math.max(...historyScores) : null;

    // Find the record with the maximum score
    const allTimeHighRecord = maxHistoryScore !== null
      ? scoreHistory.find(h => (h.totalScore || 0) === maxHistoryScore) || scoreHistory[0]
      : { totalScore: currentScore, timestamp: now.toISOString() };

    console.log('All-Time High Debug:', {
      currentScore,
      historyScores,
      maxHistoryScore,
      allTimeHighRecord: allTimeHighRecord?.totalScore,
      scoreHistoryLength: scoreHistory.length
    });

    // Calculate days as MQL (score >= 60) and SQL (score >= 80)
    let mqlDays = 0;
    let sqlDays = 0;
    const sortedHistory = [...scoreHistory].sort((a, b) =>
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    for (let i = 0; i < sortedHistory.length; i++) {
      const record = sortedHistory[i];
      const nextRecord = sortedHistory[i + 1];
      const recordDate = new Date(record.timestamp);
      const nextDate = nextRecord ? new Date(nextRecord.timestamp) : now;
      const daysDiff = (nextDate - recordDate) / (1000 * 60 * 60 * 24);

      if (record.totalScore >= 80) {
        sqlDays += daysDiff;
      } else if (record.totalScore >= 60) {
        mqlDays += daysDiff;
      }
    }

    // Check current status
    if (currentScore >= 80) {
      const lastRecord = sortedHistory[sortedHistory.length - 1];
      if (lastRecord) {
        const daysSince = (now - new Date(lastRecord.timestamp)) / (1000 * 60 * 60 * 24);
        sqlDays += daysSince;
      }
    } else if (currentScore >= 60) {
      const lastRecord = sortedHistory[sortedHistory.length - 1];
      if (lastRecord) {
        const daysSince = (now - new Date(lastRecord.timestamp)) / (1000 * 60 * 60 * 24);
        mqlDays += daysSince;
      }
    }

    return {
      currentScore,
      sevenDayChange: currentScore - sevenDaysAgoScore,
      allTimeHigh: allTimeHighRecord.totalScore,
      allTimeHighDate: allTimeHighRecord.timestamp,
      timeAsMQL: Math.round(mqlDays),
      timeAsSQL: Math.round(sqlDays)
    };
  }, [scoreData, scoreHistory]);

  if (loading) {
    const loadingContent = (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading score...</p>
      </div>
    );
    if (embedded) {
      return loadingContent;
    }
    return (
      <Card className="score-display-card">
        <Card.Body>{loadingContent}</Card.Body>
      </Card>
    );
  }

  // Hide widget completely if error or no score data
  // Note: hasScoringRule check is handled by DetailLayout, so we don't hide here
  // to prevent the component from loading and then hiding itself
  if (error || !scoreData) {
    return null;
  }

  // If no scoring rule found, still return null (but this should be caught by DetailLayout)
  if (!hasScoringRule && !loading) {
    return null;
  }

  const { totalPositive, totalNegative, totalScore } = scoreData;

  // Helper function to get actual field value from record
  const getActualFieldValue = (fieldName) => {
    if (!fieldName || !recordFields || recordFields.length === 0) {
      return null;
    }

    const field = recordFields.find(
      f => f?.fieldName?.toLowerCase() === fieldName.toLowerCase()
    );

    if (field && field.value !== null && field.value !== undefined && field.value !== '') {
      // Use displayValue if available (for lookups), otherwise use value
      return field.displayValue || field.value;
    }

    return null;
  };

  // Helper function to format condition descriptions and extract field value
  const formatConditionDescription = (description) => {
    if (!description) return { field: 'Unknown', value: '', actualValue: null, fieldName: '' };

    // Format: "field operator value" -> { field: "Field Label", value: "Value", actualValue: "Actual" }
    // Example: "lead_source equals Phone Inquiry" -> { field: "Lead Source", value: "Phone Inquiry", actualValue: "Phone Inquiry" }
    const parts = description.split(' ');

    if (parts.length >= 3) {
      const fieldName = parts[0];
      const operator = parts[1];
      // Value might contain spaces, so join everything after operator
      const expectedValue = parts.slice(2).join(' ');

      // Convert field_name to Field Name
      const formattedField = fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Get actual value from record
      const actualValue = getActualFieldValue(fieldName);

      return {
        field: formattedField,
        value: expectedValue || '',
        actualValue: actualValue,
        fieldName: fieldName
      };
    }

    return { field: description, value: '', actualValue: null, fieldName: '' };
  };

  // Calculate percentages
  const maxValue = Math.max(totalPositive, totalNegative, Math.abs(totalScore));
  const positivePercent = maxValue > 0 ? ((totalPositive / maxValue) * 100).toFixed(1) : 0;
  const negativePercent = maxValue > 0 ? ((totalNegative / maxValue) * 100).toFixed(1) : 0;

  // Calculate total score percentage using the actual maximum possible score
  // Formula: (currentScore / maxPossibleScore) * 100
  let totalScorePercent;
  const actualMaxScore = maxPossibleScore > 0 ? maxPossibleScore : 100; // Fallback to 100 if not calculated
  const percentage = (totalScore / actualMaxScore) * 100;
  // Clamp between -100 and 100 for display (in case score exceeds max)
  totalScorePercent = Math.max(-100, Math.min(100, percentage)).toFixed(1);

  // Prepare historical chart data
  const prepareHistoryChartData = () => {
    if (scoreHistory.length === 0) {
      // If no history, show current score as a single point
      return {
        labels: [new Date().toLocaleDateString()],
        datasets: [
          {
            label: 'Total Score',
            data: [totalScore],
            borderColor: totalScore >= 0 ? 'rgba(40, 167, 69, 1)' : 'rgba(220, 53, 69, 1)',
            backgroundColor: totalScore >= 0 ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
          {
            label: 'Positive Score',
            data: [totalPositive],
            borderColor: 'rgba(40, 167, 69, 0.6)',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
          },
          {
            label: 'Negative Score',
            data: [-totalNegative],
            borderColor: 'rgba(220, 53, 69, 0.6)',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
          }
        ]
      };
    }

    const labels = scoreHistory.map(h => {
      const date = new Date(h.timestamp);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    // Add current score to the end if not already there
    const lastHistoryDate = scoreHistory.length > 0
      ? new Date(scoreHistory[scoreHistory.length - 1].timestamp)
      : null;
    const today = new Date();
    const isTodayIncluded = lastHistoryDate &&
      lastHistoryDate.toDateString() === today.toDateString();

    if (!isTodayIncluded) {
      labels.push(today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }

    const totalScores = scoreHistory.map(h => h.totalScore);
    if (!isTodayIncluded) totalScores.push(totalScore);

    const positiveScores = scoreHistory.map(h => h.totalPositive || 0);
    if (!isTodayIncluded) positiveScores.push(totalPositive);

    const negativeScores = scoreHistory.map(h => -(h.totalNegative || 0));
    if (!isTodayIncluded) negativeScores.push(-totalNegative);

    // Add threshold lines as datasets
    const mqlThreshold = new Array(labels.length).fill(60);
    const sqlThreshold = new Array(labels.length).fill(80);

    // Collect events for markers
    const events = [];
    scoreHistory.forEach((h, idx) => {
      if (h.events && h.events.length > 0) {
        h.events.forEach(event => {
          if (Math.abs(event.change) >= 5) { // Only show significant changes
            events.push({
              x: idx,
              y: h.totalScore,
              change: event.change,
              label: event.field,
              timestamp: event.timestamp
            });
          }
        });
      }
    });

    return {
      labels,
      datasets: [
        {
          label: 'Total Score',
          data: totalScores,
          borderColor: 'rgba(0, 123, 255, 1)',
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgba(0, 123, 255, 1)',
        },
        {
          label: 'Positive Score',
          data: positiveScores,
          borderColor: 'rgba(40, 167, 69, 0.6)',
          backgroundColor: 'rgba(40, 167, 69, 0.05)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Negative Score',
          data: negativeScores,
          borderColor: 'rgba(220, 53, 69, 0.6)',
          backgroundColor: 'rgba(220, 53, 69, 0.05)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'MQL Threshold (60)',
          data: mqlThreshold,
          borderColor: 'rgba(255, 193, 7, 0.8)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
        {
          label: 'SQL Threshold (80)',
          data: sqlThreshold,
          borderColor: 'rgba(255, 87, 34, 0.8)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0,
        }
      ],
      events
    };
  };

  const historyChartData = prepareHistoryChartData();

  const historyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.dataset.label === 'Negative Score'
                ? Math.abs(context.parsed.y)
                : context.parsed.y;
            }
            return label;
          }
        }
      },
      // Note: Threshold lines can be added using chartjs-plugin-annotation if available
      // For now, thresholds are shown in the legend
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function (value) {
            return value;
          }
        }
      }
    }
  };

  // Prepare current score bar chart data
  const currentChartData = {
    labels: ['Positive Score', 'Negative Score'],
    datasets: [
      {
        label: 'Score Breakdown',
        data: [totalPositive, -totalNegative],
        backgroundColor: [
          'rgba(40, 167, 69, 0.8)',
          'rgba(220, 53, 69, 0.8)',
        ],
        borderColor: [
          'rgba(40, 167, 69, 1)',
          'rgba(220, 53, 69, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const currentChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed.x;
            return `${context.dataset.label || ''}: ${Math.abs(value)}`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'right',
        formatter: (value) => Math.abs(value),
        color: '#333',
        font: {
          weight: 'bold',
          size: 12,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return Math.abs(value);
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Don't render if no active scoring rule found (hide complete widget)
  if (!hasScoringRule && !loading) {
    return null;
  }

  const scoreBody = (
    <div className="score-display-body">
      {/* Total Score Display with Donut Chart - Side by Side Layout */}
      <div className="total-score-display mb-4">
            <div className="row g-4 align-items-center">
              {/* Left Side: Donut Chart */}
              <div className="col-md-6">
                <div className="donut-chart-container">
                  {(() => {
                    // Calculate percentage for donut based on actual max score
                    // Use the actual percentage calculated from maxPossibleScore
                    const actualMaxScore = maxPossibleScore > 0 ? maxPossibleScore : 100;
                    const donutPercentageValue = Math.abs((totalScore / actualMaxScore) * 100);
                    const donutPercentage = Math.min(100, Math.max(0, donutPercentageValue));
                    const remainingPercentage = 100 - donutPercentage;

                    const donutData = {
                      labels: ['Score', 'Remaining'],
                      datasets: [
                        {
                          data: [donutPercentage, remainingPercentage],
                          backgroundColor: [
                            totalScore >= 0 ? 'rgba(40, 167, 69, 0.8)' : 'rgba(220, 53, 69, 0.8)',
                            'rgba(200, 200, 200, 0.2)'
                          ],
                          borderColor: [
                            totalScore >= 0 ? 'rgba(40, 167, 69, 1)' : 'rgba(220, 53, 69, 1)',
                            'rgba(200, 200, 200, 0.3)'
                          ],
                          borderWidth: 3,
                          cutout: '75%'
                        }
                      ]
                    };

                    const donutOptions = {
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          enabled: false
                        },
                        datalabels: {
                          display: false
                        }
                      },
                      cutout: '75%'
                    };

                    return (
                      <div className="donut-wrapper">
                        <Doughnut data={donutData} options={donutOptions} />
                        <div className="donut-center-text">
                          <div className="donut-score-value" style={{ color: totalScore >= 0 ? '#28a745' : '#dc3545' }}>
                            {totalScore >= 0 ? '+' : ''}{totalScore}
                          </div>
                          <div className="donut-score-label">Total Score</div>
                          <div className="donut-score-percent" style={{ color: totalScore >= 0 ? '#28a745' : '#dc3545' }}>
                            {totalScore >= 0 ? '+' : ''}{totalScorePercent}%
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Right Side: Score Information */}
              <div className="col-md-6">
                <div className="score-info-column">
                  {/* Positive Score */}
                  <div className="score-info-card score-info-card-positive mb-3">
                    <div className="score-info-icon">
                      <i className="fas fa-arrow-up"></i>
                    </div>
                    <div className="score-info-content">
                      <div className="score-info-label">Total Positive</div>
                      <div className="score-info-value text-success">+{totalPositive}</div>
                    </div>
                  </div>

                  {/* Negative Score */}
                  <div className="score-info-card score-info-card-negative ">
                    <div className="score-info-icon">
                      <i className="fas fa-arrow-down"></i>
                    </div>
                    <div className="score-info-content">
                      <div className="score-info-label">Total Negative</div>
                      <div className="score-info-value text-danger">-{totalNegative}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Positive and Negative Points - Side by Side */}
          <div className="score-points-breakdown">
            <div className="row g-3">
              {/* Left Column: Top Positives */}
              <div className="col-md-6">
                {appliedRules.filter(rule => rule.score > 0).length > 0 ? (
                  <div className="points-section positive-points">
                    <div className="points-header">
                      <span className="points-dot positive-dot"></span>
                      <h6 className="points-title">Top Positives</h6>
                    </div>
                    <div className="points-list">
                      {appliedRules
                        .filter(rule => rule.score > 0)
                        .sort((a, b) => b.score - a.score)
                        .slice(0, 5)
                        .map((rule, idx) => {
                          const formatted = formatConditionDescription(rule.conditionDescription);
                          const displayValue = formatted.actualValue || formatted.value || 'is specified';
                          return (
                            <div key={idx} className="point-item positive-point">
                              <div className="point-description">
                                <span className="point-field">{formatted.field}:</span>
                                <span className="point-value point-value-highlight">{displayValue}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="points-section positive-points">
                    <div className="points-header">
                      <span className="points-dot positive-dot"></span>
                      <h6 className="points-title">Top Positives</h6>
                    </div>
                    <div className="no-points-message">
                      <p className="text-muted mb-0">No positive conditions met</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Top Negatives */}
              <div className="col-md-6">
                {appliedRules.filter(rule => rule.score < 0).length > 0 ? (
                  <div className="points-section negative-points">
                    <div className="points-header">
                      <span className="points-dot negative-dot"></span>
                      <h6 className="points-title">Top Negatives</h6>
                    </div>
                    <div className="points-list">
                      {appliedRules
                        .filter(rule => rule.score < 0)
                        .sort((a, b) => Math.abs(a.score) - Math.abs(b.score))
                        .reverse()
                        .slice(0, 5)
                        .map((rule, idx) => {
                          const formatted = formatConditionDescription(rule.conditionDescription);
                          const displayValue = formatted.actualValue !== null
                            ? formatted.actualValue
                            : (formatted.value || 'is not specified');
                          return (
                            <div key={idx} className="point-item negative-point">
                              <div className="point-description">
                                <span className="point-field">{formatted.field}:</span>
                                <span className="point-value point-value-highlight">{displayValue}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="points-section negative-points">
                    <div className="points-header">
                      <span className="points-dot negative-dot"></span>
                      <h6 className="points-title">Top Negatives</h6>
                    </div>
                    <div className="no-points-message">
                      <p className="text-muted mb-0">No negative conditions met</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
    </div>
  );

  if (embedded) {
    return scoreBody;
  }

  return (
    <Card className="score-display-card">
      <Card.Header className="score-display-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-chart-line me-2"></i>
            {objectName ? `${objectName.charAt(0).toUpperCase() + objectName.slice(1)} Score` : 'Score'}
          </h5>
          <button
            className="btn btn-link p-0 text-muted collapse-toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}></i>
          </button>
        </div>
      </Card.Header>
      {!isCollapsed && scoreBody}
    </Card>
  );
};

export default ScoreDisplay;
